/**
 * The Cup O' Joe podcast landing page body: hero (title, blurb, platform links, live-show
 * registration), a grid of episode cards with "Load More", the newsletter sign-up and the contact
 * form.
 *
 * Position: rendered only by
 * `src/pages/metal-resources/metal-market-intelligence/podcast/index.tsx`, which passes the
 * episode list from `src/contentful/podcast/getContentfulPodcastEpisodesData.tsx`
 * (per language, newest first). Each card links to
 * `/metal-resources/metal-market-intelligence/podcast/<slug>`, built by `podcast/[slug].tsx`.
 * Static copy comes from `./Podcast.content.tsx` → `podcastStaticContent`, the hero from
 * `./Podcast.helpers.tsx` → `PodcastHeroSection`, and styles from `./Podcast.styles.tsx`.
 *
 * Desktop and mobile are two `Media` blocks (split at `lg`) with the same markup apart from styles.
 * They share one `slicePoint` state. A change to the cards goes in both.
 *
 * The card's "Released on" label is a literal, so it shows in English in every language.
 * `PodcastDetail.tsx` translates its own label.
 */
import React from "react";
import { Language, useApplication } from "@ryerson/frontend.application";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { Icon } from "@ryerson/frontend.assets";
import { Link } from "@ryerson/frontend.navigation";
import { Typography } from "@ryerson/frontend.typography";

import { Media } from "@ryerson/frontend.next";
import { useTheme } from "@ryerson/frontend.theme";
import Moment from "react-moment";
import ContactUsForm from "@components/Shared/ContactUsRefactor/ContactUs";
import StayTunedComponent from "@components/Shared/StayTuned/StayTuned";
import { TranslatedPodcastNodes } from "@contentful/podcast/types";
import { podcastStaticContent as staticContent } from "./Podcast.content";
import * as PodcastStyles from "./Podcast.styles";

import { PodcastHeroSection } from "./Podcast.helpers";

/**
 * An older copy of `./Podcast.content.tsx` → `PodcastStaticContent`, without
 * `registerForLiveShow`. Nothing imports it; the copy this component reads is typed by the
 * `Podcast.content.tsx` one.
 */
export type PodcastStaticContent = {
	title: string;
	blurb: string;
	backgroundImage: string;
	imageUrl: string;
	followUs: string;
	apple: string;
	spotify: string;
	amazon: string;
	youtube: string;
	episodes: string;
	viewEpisode: string;
	loadMore: string;
};

// Styled component extraction
const { LoadMore } = PodcastStyles;

/** The newsletter sign-up's `parent`, sent as the value of its `stay_tuned` GA event. */
const STAY_TUNED_PARENT = "Podcasts-Landing";
/** Episode cards shown before "Load More"; each click shows three more. */
const SLICE_POINT = 9;

interface PodcastProps {
	/** Every episode per language, newest first. */
	podcastContentfulData: TranslatedPodcastNodes;
}

/**
 * The podcast landing page body. Renders the visitor's language's episode list, all of it
 * built into the page; "Load More" only reveals more cards.
 */
const Podcasts: React.FC<PodcastProps> = ({ podcastContentfulData }) => {
	const {
		localization: { language },
	} = useApplication();
	const { theme } = useTheme();

	// Note currently the data is only in English in Contentful
	// The `en` fallback applies only when the language key is absent. The fetcher always returns
	// all three keys, and an empty array is truthy, so it doesn't fire for an empty list.
	let podcastContent = !!podcastContentfulData[language]
		? podcastContentfulData[language]
		: podcastContentfulData.en;

	const [slicePoint, setSlicePoint] = React.useState<number>(SLICE_POINT);

	// Hero: Calculate dynamic height based on language state
	// The French copy is longer and overflowed the 410px desktop hero, so French gets 470px.
	// `vPadding` is half the height: `ContentSection` pads top and bottom, which reserves the
	// space the absolutely positioned hero image and overlay cover. The mobile hero ignores both.
	const heroHeight = language === Language.fr ? "470px" : "410px";
	const vPadding = language === Language.fr ? "235px" : "205px";

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<PodcastHeroSection
					staticContent={staticContent[language]}
					theme={theme}
					heroHeight={heroHeight}
					vPadding={vPadding}
					isMobile={false}
				/>
				<ContentSection type="primary" vPadding="40px">
					<Typography variant="h3" type="primary" css={PodcastStyles.EpisodesTitleCSS}>
						{staticContent[language].episodes}
					</Typography>
					<Flex
						direction="row"
						alignItems="center"
						justifyContent="flex-start"
						wrap="wrap"
						css={PodcastStyles.EpisodesGridDesktopCSS}
					>
						{podcastContent.slice(0, slicePoint).map((podcast: any) => (
							<React.Fragment key={podcast.title}>
								<FlexItem
									css={PodcastStyles.EpisodeCardDesktopCSS(
										theme.colors.secondary.background
									)}
								>
									<img
										src={podcast.videoThumbnail.file.url}
										alt={podcast.title}
										css={PodcastStyles.EpisodeThumbnailCSS}
									/>
									<Typography
										variant="div"
										size="xs"
										type="secondary"
										css={PodcastStyles.EpisodeDateCSS}
									>
										Released on{" "}
										<Moment format="MMM D, YYYY">{podcast.publishDate}</Moment>
									</Typography>
									<Typography
										variant="h5"
										type="secondary"
										css={PodcastStyles.EpisodeTitleCSS}
									>
										{podcast.title}
									</Typography>
									<Link
										to={`/metal-resources/metal-market-intelligence/podcast/${podcast.slug}`}
									>
										<div
											css={PodcastStyles.ViewEpisodeIconCSS(
												theme.colors.primary.primaryBrand
											)}
										>
											<Icon
												icon="chevron-right"
												color={theme.colors.primary.white}
												size="xs"
											/>
										</div>
										<Typography
											variant="div"
											type="secondary"
											size="sm"
											weight="bold"
											color={theme.colors.primary.header}
											css={PodcastStyles.ViewEpisodeTextCSS}
										>
											{staticContent[language].viewEpisode}
										</Typography>
									</Link>
								</FlexItem>
							</React.Fragment>
						))}
					</Flex>
					{slicePoint < podcastContent.length && (
						<LoadMore
							css={PodcastStyles.LoadMoreDesktopCSS(theme.colors.primary.gray)}
							onClick={() => {
								setSlicePoint(slicePoint + 3);
							}}
						>
							<Typography
								variant="span"
								color={theme.colors.primary.darkGray}
								weight="bold"
								size="sm"
							>
								{staticContent[language].loadMore}
							</Typography>
						</LoadMore>
					)}
				</ContentSection>
				<StayTunedComponent parent={STAY_TUNED_PARENT} parentCategory={""} />
				<ContactUsForm background="primary" withImage={false} />
				<ContentSection type="primary" vPadding="20px"></ContentSection>
			</Media>
			<Media lessThan="lg">
				<PodcastHeroSection
					staticContent={staticContent[language]}
					theme={theme}
					heroHeight={heroHeight}
					vPadding={vPadding}
					isMobile={true}
				/>
				<ContentSection type="primary" vPadding="30px">
					<Typography variant="h3" type="primary" css={PodcastStyles.EpisodesTitleCSS}>
						{staticContent[language].episodes}
					</Typography>
					<Flex
						direction="row"
						alignItems="center"
						justifyContent="flex-start"
						wrap="wrap"
						css={PodcastStyles.EpisodesGridMobileCSS}
					>
						{podcastContent.slice(0, slicePoint).map((podcast: any) => (
							<React.Fragment key={podcast.title}>
								<FlexItem
									css={PodcastStyles.EpisodeCardMobileCSS(
										theme.colors.secondary.background
									)}
								>
									<img
										src={podcast.videoThumbnail.file.url}
										alt={podcast.title}
										css={PodcastStyles.EpisodeThumbnailCSS}
									/>
									<Typography
										variant="div"
										size="xs"
										type="secondary"
										css={PodcastStyles.EpisodeDateCSS}
									>
										Released on{" "}
										<Moment format="MMM D, YYYY">{podcast.publishDate}</Moment>
									</Typography>
									<Typography
										variant="h5"
										type="secondary"
										css={PodcastStyles.EpisodeTitleCSS}
									>
										{podcast.title}
									</Typography>
									<Link
										to={`/metal-resources/metal-market-intelligence/podcast/${podcast.slug}`}
									>
										<div
											css={PodcastStyles.ViewEpisodeIconCSS(
												theme.colors.primary.primaryBrand
											)}
										>
											<Icon
												icon="chevron-right"
												color={theme.colors.primary.white}
												size="xs"
											/>
										</div>
										<Typography
											variant="div"
											type="secondary"
											size="sm"
											weight="bold"
											color={theme.colors.primary.header}
											css={PodcastStyles.ViewEpisodeTextCSS}
										>
											{staticContent[language].viewEpisode}
										</Typography>
									</Link>
								</FlexItem>
							</React.Fragment>
						))}
					</Flex>
					{slicePoint < podcastContent.length && (
						<LoadMore
							css={PodcastStyles.LoadMoreMobileCSS(theme.colors.primary.gray)}
							onClick={() => {
								setSlicePoint(slicePoint + 3);
							}}
						>
							<Typography
								variant="span"
								color={theme.colors.primary.darkGray}
								weight="bold"
								size="sm"
							>
								{staticContent[language].loadMore}
							</Typography>
						</LoadMore>
					)}
				</ContentSection>
				<StayTunedComponent parent={STAY_TUNED_PARENT} parentCategory={""} />
				<ContactUsForm background="primary" withImage={false} />
				<ContentSection type="primary" vPadding="10px"></ContentSection>
			</Media>
		</>
	);
};

export default Podcasts;
